import React, { useState } from 'react';
import styled from 'styled-components';
import theme from 'theme';
import { useMutation, useQueryCache } from 'react-query';
import queryKeys from 'config/queryKeys';
import { shallowEqual, useSelector } from 'react-redux';

// API
import * as remoteApi from 'api/remote';
import KoalaButton from 'koala/components/Button';
import KoalaLoader from 'koala/components/Loader';
import ReactTooltip from 'react-tooltip';
import { Trans, useTranslation } from 'react-i18next';
import TabbySparkle from 'components/_assets/coach_tabby.png';

const Container = styled.div`
  padding: ${theme.spacing.x2} ${theme.spacing.x3};
  background: ${theme.colors.N3};
  border-radius: 4px;
  display: flex;
  gap: ${theme.spacing.x2};
`;

const TabbyImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
`;

const UpgradeContainer = styled.div`
  background: ${theme.colors.V5};
  padding: ${theme.spacing.x2} ${theme.spacing.x3};
  display: flex;
  gap: ${theme.spacing.x2};
  border-radius: 4px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.x1};
`;

const Info = styled.div`
  background-color: ${theme.colors.B5};
  color: ${theme.colors.B80};
  padding: ${theme.spacing.x1};
  font-size: 1.2rem;
  border-radius: 4px;
  margin: ${theme.spacing.x1} 0;
  a {
    color: ${theme.colors.B50};
    text-decoration: underline;
  }
`;

interface Props {
  outcome: any;
}

function InfoTab(props: Props) {
  const { outcome } = props;
  const queryCache = useQueryCache();
  const currentPing = useSelector((state: any) => state.session.currentPing, shallowEqual);
  const currentWorkspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t } = useTranslation();
  const translationKey = 'panels.outcome';

  let usage = null;

  if (currentPing) {
    usage = currentPing.usage;
  }

  let ai_credits_used = 0;
  let billing_ai_credits_limit = 0;

  if (usage !== null) {
    billing_ai_credits_limit = usage.billing_ai_credits_limit;
    ai_credits_used = usage.ai_credits_used;
  }

  const hasCredit = billing_ai_credits_limit > ai_credits_used;

  // implement a logic that updates the button status to say "Reviewing past updates..." then "Estimating future progress..." then "Generating checkin..."
  // This text should cycle every 3 seconds while generating the checkin
  const buttonStatuses = [
    t(`${translationKey}.reviewingPastUpdates`),
    t(`${translationKey}.estimatingFuture`),
    t(`${translationKey}.generatingCoachingCheckin`),
  ];
  const [buttonStatusIndex, setButtonStatusIndex] = useState(0);

  let textInterval: any = null;

  const [generateCoachingCheckinMutation, { isLoading: isCoachingLoading }] = useMutation(
    remoteApi.generateCoachingCheckin,
    {
      onMutate: () => {
        setButtonStatusIndex(0);
        textInterval = setInterval(() => {
          // stop the interval if the button status index is the last one
          if (buttonStatusIndex === buttonStatuses.length - 1) {
            clearInterval(textInterval);
          } else {
            setButtonStatusIndex((prevIndex) => prevIndex + 1);
          }
        }, 3000);
      },
      onSuccess: () => {
        queryCache.invalidateQueries([queryKeys.currentOutcome, outcome.nano_slug]);
        queryCache.invalidateQueries([queryKeys.checkins]);
        queryCache.invalidateQueries(queryKeys.currentPing);
        if (textInterval) {
          clearInterval(textInterval);
        }
      },
    },
  );

  const handleGenerateCoachingCheckin = () => {
    generateCoachingCheckinMutation({
      outcomeId: outcome.id,
    });
  };

  if (usage === null) {
    return <KoalaLoader />;
  }

  if (currentWorkspace.billing_subscription_plan === 'tability-v4-free') {
    return (
      <UpgradeContainer>
        <TabbyImage src={TabbySparkle} alt="Tabby Sparkle" />
        <Content>
          <h3>{t(`${translationKey}.tabbysAdvice`)}</h3>
          <p className="subtle">{t(`${translationKey}.coachingNotAvailable`)}</p>
        </Content>
      </UpgradeContainer>
    );
  }

  // Don't display coach for kpis and no metrics
  if (['kpi', 'no_metric'].includes(outcome.outcome_type)) {
    return (
      <Container>
        <TabbyImage src={TabbySparkle} alt="Tabby Sparkle" />
        <Content>
          <h3>{t(`${translationKey}.tabbysAdvice`)}</h3>
          {!hasCredit && (
            <Info>
              <Trans
                i18nKey={`${translationKey}.creditsUpgrade`}
                components={{
                  a: (
                    <a
                      href="https://guides.tability.app/docs/other/how-to-get-more-ai-credits"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      text
                    </a>
                  ),
                }}
              />
            </Info>
          )}
          <small className="subtle">{t(`${translationKey}.coachingNoGoal`)}</small>
          <div>
            <KoalaButton onClick={handleGenerateCoachingCheckin} size="small" appearance="special" disabled={true}>
              {t(`${translationKey}.generateCoachingCheckin`)}
            </KoalaButton>
          </div>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <TabbyImage src={TabbySparkle} alt="Tabby Sparkle" />
      <Content>
        <h3>{t(`${translationKey}.tabbysAdvice`)}</h3>
        <small>{t(`${translationKey}.analysisInfo`)}</small>
        <div data-tip data-for="coachAction">
          <KoalaButton
            onClick={handleGenerateCoachingCheckin}
            size="small"
            appearance="special"
            loading={isCoachingLoading}
            disabled={isCoachingLoading || !hasCredit}
          >
            {t(`${translationKey}.generateCoachingCheckin`)}
          </KoalaButton>
          &nbsp;
          {isCoachingLoading && <small>{buttonStatuses[buttonStatusIndex]}</small>}
        </div>
        {!hasCredit && (
          <ReactTooltip place="bottom" type="dark" id="coachAction" className="tooltip" effect="solid" delayShow={100}>
            {t(`${translationKey}.notEnoughCredits`)}
          </ReactTooltip>
        )}
      </Content>
    </Container>
  );
}

export default React.memo(InfoTab);
